import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
} from 'react-native';
import { REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import { SVG_COMPONENTS } from '@/assets/admin';
import { IMaintenanceListResponse } from '@/store/api/maintenanceApi';
import { DesktopSidePanel } from '@/components/common/admin/desktop/panel/DesktopSidePanel';
import { buildStatusTimeline } from '@/utils/residents/maintenance/statusTimeline';
import { getTicketTitleAndBody } from '@/utils/residents/maintenance';
import { StatusTimeline } from '@/components/residents/maintenance/shared/StatusTimeline';
import { MaintenanceImageCarousel } from '@/components/residents/maintenance/shared/MaintenanceImageCarousel';
import { WorkRequestInfoSection } from '@/components/residents/maintenance/shared/WorkRequestInfoSection';
import { ChatCTACard } from '@/components/residents/maintenance/shared/ChatCTACard';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { AnimatedEntrance } from '@/components/shared/redesign';
import { useReducedMotion } from '@/hooks/shared/useReducedMotion';

const CloseIcon = SVG_COMPONENTS.CANCEL_X;

interface DesktopMaintenanceDetailPanelProps {
  ticket: IMaintenanceListResponse | null;
  visible: boolean;
  onClose: () => void;
  onStartChat?: () => void;
  chatLoading?: boolean;
}

export const DesktopMaintenanceDetailPanel: React.FC<DesktopMaintenanceDetailPanelProps> = ({
  ticket,
  visible,
  onClose,
  onStartChat,
  chatLoading = false,
}) => {
  // Render the panel shell unconditionally so it owns the slide in AND out.
  // The ticket stays set through close (the screen only flips `visible`), so
  // the outgoing content renders while GlassSidePanel plays its exit slide;
  // GlassSidePanel unmounts the content once the animation finishes.
  return (
    <DesktopSidePanel
      visible={visible}
      onClose={onClose}
      width={400}
    >
      {ticket && (
        <DetailPanelContent
          ticket={ticket}
          onClose={onClose}
          onStartChat={onStartChat}
          chatLoading={chatLoading}
        />
      )}
    </DesktopSidePanel>
  );
};

interface DetailPanelContentProps {
  ticket: IMaintenanceListResponse;
  onClose: () => void;
  onStartChat?: () => void;
  chatLoading?: boolean;
}

const DetailPanelContent: React.FC<DetailPanelContentProps> = ({
  ticket,
  onClose,
  onStartChat,
  chatLoading = false,
}) => {
  const { colors } = useThemeColors();
  const reduceMotion = useReducedMotion();

  const timeline = buildStatusTimeline(ticket);
  const images: string[] = ticket.files?.map(f => f.fileUrl).filter(Boolean) || [];
  const { title, body } = getTicketTitleAndBody(ticket);

  return (
    <View style={[styles.panelContent, { backgroundColor: colors.card.bg }]}>
        <View style={[styles.header, { borderBottomColor: colors.card.border, backgroundColor: colors.card.bg }]}>
          <Text style={[styles.headerTitle, { color: colors.text.primary }]}>Details</Text>
          <TouchableOpacity
            style={styles.closeButton}
            onPress={onClose}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <CloseIcon width={16} height={16} color={colors.text.muted} />
          </TouchableOpacity>
        </View>

        <ScrollView
          style={[styles.content, { backgroundColor: colors.card.bg }]}
          contentContainerStyle={styles.contentContainer}
          showsVerticalScrollIndicator={false}
        >
          <AnimatedEntrance key={`status-${ticket.maintenanceRequestId}`} index={0} disabled={reduceMotion}>
            <View style={[styles.statusSection, { borderBottomColor: colors.card.border }]}>
              <Text style={[styles.sectionTitle, { color: colors.text.primary }]}>Status</Text>
              <StatusTimeline nodes={timeline} />
            </View>
          </AnimatedEntrance>

          <View style={[styles.divider, { backgroundColor: colors.separator }]} />

          <AnimatedEntrance key={`problem-${ticket.maintenanceRequestId}`} index={1} disabled={reduceMotion}>
            <View style={styles.section}>
              <Text style={[styles.sectionTitle, { color: colors.text.primary }]}>Problem</Text>
              <MaintenanceImageCarousel images={images} variant="desktop" />
              <View style={styles.problemDescription}>
                <Text style={[styles.problemTitle, { color: colors.text.primary }]}>{title}</Text>
                {body ? (
                  <Text style={[styles.problemText, { color: colors.text.muted }]}>
                    {body}
                  </Text>
                ) : null}
              </View>
            </View>
          </AnimatedEntrance>

          <AnimatedEntrance key={`work-${ticket.maintenanceRequestId}`} index={2} disabled={reduceMotion}>
            <WorkRequestInfoSection ticket={ticket} variant="desktop" />
          </AnimatedEntrance>

          <AnimatedEntrance key={`chat-${ticket.maintenanceRequestId}`} index={3} disabled={reduceMotion}>
            <ChatCTACard onPress={onStartChat} variant="desktop" loading={chatLoading} />
          </AnimatedEntrance>
        </ScrollView>
      </View>
  );
};

const styles = StyleSheet.create({
  panelContent: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
    paddingVertical: 20,
    borderBottomWidth: 1,
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: '700',
    lineHeight: 36,
  },
  closeButton: {
    width: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    flex: 1,
  },
  contentContainer: {
    paddingBottom: 32,
  },
  statusSection: {
    paddingHorizontal: 24,
    paddingVertical: 24,
    gap: 16,
    borderBottomWidth: 1,
  },
  section: {
    paddingHorizontal: 24,
    paddingVertical: 24,
    gap: 16,
  },
  sectionTitle: {
    ...REDESIGN_TYPOGRAPHY.cardTitleLarge,
    fontWeight: '400',
    lineHeight: 24,
  },
  divider: {
    height: 1,
  },
  problemDescription: {
    gap: 4,
  },
  problemTitle: {
    ...REDESIGN_TYPOGRAPHY.small,
    lineHeight: 21,
  },
  problemText: {
    ...REDESIGN_TYPOGRAPHY.body,
    lineHeight: 21,
  },
});

export default DesktopMaintenanceDetailPanel;
